import { useState, useRef, useEffect } from "react";

import { checkBinLocation } from "../../../label_print/src/model/PrintingModel";


const normalizeBin = (v) => (v ?? "").toString().trim().toUpperCase();

const useBinDuplicateCheck = (material) => {
  const [isCheckingBin, setIsCheckingBin] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    binLocation: "",
    materials: [],
  });

  const debounceRef = useRef(null);
  const checkIdRef = useRef(0);
  const lastWarnedBinRef = useRef("");
  const materialRef = useRef(material);
  materialRef.current = material;

  // clear pending timer on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Looks the bin up in the DB and returns the OTHER materials using it
  const getDuplicateMaterials = async (value) => {
    const newBin = normalizeBin(value);
    if (!newBin) return [];

    try {
      const response = await checkBinLocation(
        String(value).trim(),
        materialRef.current
      );
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      return [
        ...new Set(
          list
            .filter(
              (item) =>
                normalizeBin(item.bin_location) === newBin &&
                item.material_no !== materialRef.current
            )
            .map((item) => item.material_no)
        ),
      ];
    } catch (error) {
      console.log("error while checking bin location", error);
      return []; // fail open: don't block the user if the check fails
    }
  };

  const runCheck = async (value, checkId) => {
    try {
      const materials = await getDuplicateMaterials(value);

      // user typed again while this was running: ignore this result
      if (checkId !== checkIdRef.current) return;

      const bin = normalizeBin(value);
      if (materials.length === 0) {
        lastWarnedBinRef.current = "";
        return;
      }
      if (lastWarnedBinRef.current === bin) return; // already warned for this bin

      lastWarnedBinRef.current = bin;
      setDuplicateDialog({ open: true, binLocation: value, materials });
    } finally {
      if (checkId === checkIdRef.current) setIsCheckingBin(false);
    }
  };

  // call this on every change of the bin location input
  const handleBinChange = (value) => {
    clearTimeout(debounceRef.current);
    checkIdRef.current += 1; // cancels any search already running
    const checkId = checkIdRef.current;

    if (!normalizeBin(value)) {
      setIsCheckingBin(false);
      lastWarnedBinRef.current = "";
      return;
    }

    setIsCheckingBin(true); // buttons disable as soon as the user types
    debounceRef.current = setTimeout(() => runCheck(value, checkId), 500);
  };

  const closeDuplicateDialog = () =>
    setDuplicateDialog({ open: false, binLocation: "", materials: [] });

  // call when the popup closes
  const resetBinCheck = () => {
    clearTimeout(debounceRef.current);
    checkIdRef.current += 1;
    lastWarnedBinRef.current = "";
    setIsCheckingBin(false);
    closeDuplicateDialog();
  };

  return {
    isCheckingBin,
    duplicateDialog,
    handleBinChange,
    closeDuplicateDialog,
    resetBinCheck,
  };
};

export default useBinDuplicateCheck;